<template>
  <vl-region class="region">
    <vl-layout>
      <template v-if="detail && !loading">
        <vl-link-list mod-inline>
          <vl-link-list-item>
            <vl-link :to="`/publication/${id}`" mod-block mod-bold mod-no-visited-state :exact="true">
              {{ getPublicationLabel() }}
            </vl-link>
          </vl-link-list-item>
        </vl-link-list>
        <vl-title tag-name="h1">{{
          isDatasetserie ? $t('publications.create.newDataset') : $t('publications.create.newVersion')
        }}</vl-title>
        <vl-grid v-if="specifications">
          <vl-column width="12">
            <CreatePublicationForm
              :new-publication-check="newPublicationCheck"
              :file-required="isFileRequired"
              :specifications="specifications"
              :saving="saving"
              :uploadProgress="uploadProgress"
              @cancel="onFormCanceled"
              @submit="onFormSubmitted"
            ></CreatePublicationForm>
          </vl-column>
        </vl-grid>
      </template>
      <template v-if="loading">
        <vl-region>
          <div v-vl-align:center>
            <vl-loader :message="$t('general.pageLoading')" />
          </div>
        </vl-region>
      </template>
    </vl-layout>
  </vl-region>
</template>
<script lang="ts" setup>
import { onMounted, onUnmounted, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePublicationDetailStore } from '../store/publication-detail.store';
import { ENUM_PublicationTypes } from '@/modules/core/common';
import CreatePublicationForm from './subcomponents/create-publication-form/CreatePublicationForm.vue';
import { useProductSpecificationStore } from '@/modules/products/store/product-specification.store';
import { CreatePublication } from '@/api/frontoffice/clients';
import { usePublicationCreateStore } from '../store/publication-create.store';

const detailStore = usePublicationDetailStore();
const createStore = usePublicationCreateStore();
const productSpecificationStore = useProductSpecificationStore();

const route = useRoute();
const router = useRouter();

const id = computed((): string => {
  return route.params.id as string;
});

const detail = computed(() => {
  return detailStore.publication;
});

const newPublicationCheck = computed(() => {
  return detail.value ? detail.value.newPublicationCheck : null;
});

const isDatasetserie = computed((): boolean => {
  return detail.value.publicationType === ENUM_PublicationTypes.Datasetserie.id;
});

const isFileRequired = computed((): boolean => {
  return !detail.value.deliverySkipUpload;
});

const getPublicationLabel = (): string => {
  return detail.value.productLabel;
};

const loading = computed((): boolean => {
  return detailStore.loading || productSpecificationStore.loading;
});

const saving = computed((): boolean => {
  return createStore.saving;
});

const uploadProgress = computed((): number => {
  return createStore.uploadProgress;
});

const specifications = computed(() => {
  return productSpecificationStore.specifications;
});

const onFormCanceled = () => {
  router.go(-1);
};

const onFormSubmitted = async (values) => {
  const body = <CreatePublication>{
    deliveryDefinitionId: detail.value.deliveryDefinitionId,
    productDefinitionId: detail.value.productDefinitionId,
    products: values.products.map((product) => ({
      productSpecificationId: product.specification.id,
      versionDate: product.versionDate ? product.versionDate.toISOString() : null,
      versionDescription: product.versionDescription,
      temporalLimitationFrom: product.temporalLimitationFrom ? product.temporalLimitationFrom.toISOString() : null,
      temporalLimitationTo: product.temporalLimitationTo ? product.temporalLimitationTo.toISOString() : null
    }))
  };

  const created = await createStore.createPublication(body, values.file);

  if (created) {
    router.push({ name: 'publication.detail', params: { id: id.value } });
  }
};

onMounted(async () => {
  await detailStore.getPublication(id.value);

  if (detail.value) {
    await productSpecificationStore.getProductsForDelivery(id.value);
  }
});

onUnmounted(() => {
  detailStore.clear();
  productSpecificationStore.clear();
  createStore.clear();
});
</script>

<style lang="scss" scoped></style>
